import React, { useEffect, useState } from 'react';
import documentService from '../../services/documentService';
import DocumentUploadModal from '../../components/Documents/DocumentUploadModal';
import { decodeHtmlEntities } from '../../utils/textUtils';
import { 
  FileText, Upload, Download, Trash2, Search, X,
  AlertCircle, RefreshCw, File, Image,
  FileSpreadsheet, FileType, Calendar, Loader2, Building
} from 'lucide-react';
import '../../styles/modern-design-system.css';

const AdminDocumentsPage = () => {
  const [activeTab, setActiveTab] = useState('trip'); // 'trip' or 'agency'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Trip Documents state
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [loadingTrips, setLoadingTrips] = useState(false);

  // Load trips for trip selector (only when trip tab is active)
  useEffect(() => {
    const loadTrips = async () => {
      if (activeTab === 'trip') {
        try {
          setLoadingTrips(true);
          const apiUrl = import.meta.env.VITE_API_URL;
          if (!apiUrl) {
            throw new Error('VITE_API_URL is not configured');
          }
          const response = await fetch(`${apiUrl}/api/trips`, {
            credentials: 'include',
          });
          if (!response.ok) throw new Error('Failed to fetch trips');
          const tripsData = await response.json();
          setTrips(Array.isArray(tripsData) ? tripsData : []);
        } catch (err) {
          console.error('Error loading trips:', err);
          setError('Failed to load trips');
        } finally {
          setLoadingTrips(false);
        }
      }
    };
    loadTrips();
  }, [activeTab]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await documentService.getCategories();
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load documents based on active tab
  useEffect(() => {
    if (activeTab === 'trip' && !selectedTripId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTripId, selectedCategory]);

  const loadDocuments = async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (activeTab === 'trip') {
        if (!selectedTripId) {
          setDocuments([]);
          return;
        }
        // Load trip documents
        const response = await documentService.getAllDocuments(
          selectedCategory || null,
          'TRIP',
          selectedTripId
        );
        setDocuments(response.data.documents || []);
      } else {
        // Load agency documents
        const response = await documentService.getAllDocuments(
          selectedCategory || null,
          'AGENCY',
          null
        );
        setDocuments(response.data.documents || []);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleUploadSuccess = (newDocument) => {
    setDocuments(prev => [newDocument, ...prev]);
    setShowUploadModal(false);
  };

  const handleDownload = async (documentId) => {
    try {
      const response = await documentService.getDownloadUrl(documentId);
      window.open(response.data.downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentService.deleteDocument(documentId);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType?.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <FileType className="w-5 h-5 text-blue-600" />;
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.originalName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.confirmationNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const selectedTrip = trips.find(t => t.tripid === selectedTripId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wonder Vault</h1>
          <p className="mt-1 text-gray-600">Manage trip documents and agency business files</p>
        </div>
        <div className="flex gap-3 items-center">
          <button
            onClick={() => loadDocuments(true)}
            disabled={refreshing || (activeTab === 'trip' && !selectedTripId)}
            className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            disabled={activeTab === 'trip' && !selectedTripId}
            className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4" aria-label="Tabs">
          <button
            onClick={() => {
              setActiveTab('trip');
              setSelectedTripId(null);
            }}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'trip'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex gap-2 items-center">
              <Calendar className="w-4 h-4" />
              Trip Documents
            </div>
          </button>
          <button
            onClick={() => setActiveTab('agency')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'agency'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex gap-2 items-center">
              <Building className="w-4 h-4" />
              Agency Vault
            </div>
          </button>
        </nav>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex gap-2 items-center p-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="flex-shrink-0 w-5 h-5" />
          <span>{error}</span>
          <button
            onClick={() => loadDocuments()}
            className="ml-auto text-sm font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Trip Documents Tab */}
      {activeTab === 'trip' && (
        <div className="space-y-4">
          {/* Trip Selector */}
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <label htmlFor="trip-select" className="block mb-2 text-sm font-medium text-gray-700">
              Select Trip
            </label>
            {loadingTrips ? (
              <div className="flex gap-2 items-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading trips...</span>
              </div>
            ) : (
              <select
                id="trip-select"
                value={selectedTripId || ''}
                onChange={(e) => setSelectedTripId(e.target.value || null)}
                className="px-3 py-2 w-full max-w-md rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select a trip --</option>
                {trips.map((trip) => (
                  <option key={trip.tripid} value={trip.tripid}>
                    {decodeHtmlEntities(trip.tripname)} ({trip.destination})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Filters */}
          {selectedTripId && (
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search trip documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="sm:w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Agency Vault Tab */}
      {activeTab === 'agency' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search agency documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {((activeTab === 'trip' && selectedTripId) || activeTab === 'agency') && loading && !refreshing && (
        <div className="flex justify-center items-center py-12 bg-white rounded-lg border border-gray-200">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      )}

      {/* Empty States */}
      {activeTab === 'trip' && !selectedTripId && (
        <div className="py-12 text-center bg-white rounded-lg border border-gray-200">
          <Calendar className="mx-auto mb-4 w-16 h-16 text-gray-300" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Select a Trip</h3>
          <p className="text-gray-600">Choose a trip from the dropdown above to view and upload trip documents.</p>
        </div>
      )}

      {activeTab === 'trip' && selectedTripId && !loading && filteredDocuments.length === 0 && (
        <div className="py-12 text-center bg-white rounded-lg border border-gray-200">
          <FileText className="mx-auto mb-4 w-16 h-16 text-gray-300" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No trip documents found</h3>
          <p className="mb-4 text-gray-600">
            {searchQuery || selectedCategory
              ? 'Try adjusting your search or filter criteria'
              : 'Upload your first document for this trip to get started'
            }
          </p>
          {!searchQuery && !selectedCategory && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex gap-2 items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </button>
          )}
        </div>
      )}

      {activeTab === 'agency' && !loading && filteredDocuments.length === 0 && (
        <div className="py-12 text-center bg-white rounded-lg border border-gray-200">
          <Building className="mx-auto mb-4 w-16 h-16 text-gray-300" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No agency documents found</h3>
          <p className="mb-4 text-gray-600">
            {searchQuery || selectedCategory
              ? 'Try adjusting your search or filter criteria'
              : 'Upload your first agency document to get started'
            }
          </p>
          {!searchQuery && !selectedCategory && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex gap-2 items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </button>
          )}
        </div>
      )}

      {/* Documents Table */}
      {((activeTab === 'trip' && selectedTripId) || activeTab === 'agency') && !loading && filteredDocuments.length > 0 && (
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Document</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Size</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Uploaded</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Uploaded By</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocuments.map((document) => {
                const categoryInfo = categories.find(cat => cat.id === document.category) || { name: document.category, icon: '📄' };
                return (
                  <tr key={document.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative flex gap-3 items-center">
                        <button
                          type="button"
                          onClick={() => handleDelete(document.id)}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove document"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="flex gap-3 items-center pl-7">
                          {getFileIcon(document.mimeType)}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{document.originalName}</div>
                            {document.description && (
                              <div className="text-sm text-gray-500">{document.description}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2 items-center">
                        <span className="text-lg">{categoryInfo.icon}</span>
                        <span className="text-sm text-gray-700">{categoryInfo.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatFileSize(document.fileSize)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(document.uploadedAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {document.uploadedBy?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                      <div className="flex gap-2 justify-end items-center">
                        <button
                          onClick={() => handleDownload(document.id)}
                          className="p-1 text-blue-600 rounded-md transition-colors hover:text-blue-900 hover:bg-blue-50"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(document.id)}
                          className="p-1 text-red-600 rounded-md transition-colors hover:text-red-900 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
        tripId={activeTab === 'trip' ? selectedTripId || undefined : undefined}
        clientId={undefined}
        clientName={activeTab === 'trip' && selectedTripId ? decodeHtmlEntities(selectedTrip?.tripname) || 'trip' : 'agency'}
      />
    </div>
  );
};

export default AdminDocumentsPage;

